import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { quotesService } from "@/api/services/quotes.service";
import { contactsService } from "@/api/services/contacts.service";
import { additionalWorksService } from "@/api/services/additional-works.service";
import { jobsService } from "@/api/services/jobs.service";
import { QuoteApprovalModal, CancelQuoteDialog, QuoteDeclineReasonModal, } from "./components";
import { EmptyState, LoopingImage } from "@/components/common";
import { formatFullName } from "@/utils/string";
import { formatQuoteAddress } from "@/utils/address";
import { useAlert } from "@/hooks/useAlert";
import { matchesQuoteStatus, ADDITIONAL_WORKS_QUOTE_TYPE, } from "@/utils/quoteHelpers";
import { toDateTimeLocal } from "@/utils/dateTime";
import styles from "./ClientQuotesPage.module.css";
import emptyStateStyles from "@/components/common/EmptyState/EmptyState.module.css";
const DeleteIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "15", y1: "9", x2: "9", y2: "15" }), _jsx("line", { x1: "9", y1: "9", x2: "15", y2: "15" })] }));
const ChatIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" }) }));
const DocumentIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("polyline", { points: "14 2 14 8 20 8" }), _jsx("line", { x1: "16", y1: "13", x2: "8", y2: "13" }), _jsx("line", { x1: "16", y1: "17", x2: "8", y2: "17" }), _jsx("polyline", { points: "10 9 9 9 8 9" })] }));
export const ClientQuotesPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { alert } = useAlert();
    // Data state
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [contactDetails, setContactDetails] = useState(null);
    // UI state
    const [approvalModalOpen, setApprovalModalOpen] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [quoteToCancel, setQuoteToCancel] = useState(null);
    const [quoteToCancelIndex, setQuoteToCancelIndex] = useState(undefined);
    const [quoteToDecline, setQuoteToDecline] = useState(null);
    // Fetch contact details for auto-fill
    const fetchContactDetails = useCallback(async () => {
        if (!user?.id)
            return;
        try {
            const response = await contactsService.getContact(user.id);
            const contact = response.data;
            setContactDetails({
                name: formatFullName(contact.firstName, contact.lastName),
                email: contact.email,
                position: contact.role || "",
                company: contact.companyName || "",
            });
        }
        catch (err) {
            console.error("Error fetching contact details:", err);
            // Fall back to user context
            setContactDetails({
                name: user?.name || "",
                email: user?.email || "",
                position: "",
                company: "",
            });
        }
    }, [user?.id, user?.name, user?.email]);
    // Fetch quotes for the current client
    const fetchQuotes = useCallback(async () => {
        if (!user?.id)
            return;
        setLoading(true);
        setError(null);
        try {
            const response = await quotesService.getQuotesByContact(user.id);
            // Handle successful response - even if data is empty array
            setQuotes(response.data || []);
        }
        catch (err) {
            console.error("Error fetching quotes:", err);
            // Only show error for actual failures, not for "no data" scenarios
            // 404 or empty responses are handled as empty arrays by the backend now
            if (err?.response?.status === 404 || err?.response?.status === 204) {
                // Treat as "no quotes" - not an error
                setQuotes([]);
            }
            else if (err?.code === "ECONNABORTED" || err?.code === "ERR_NETWORK") {
                // Network errors
                setError("Unable to connect to the server. Please check your connection and try again.");
            }
            else {
                // Other errors
                setError("Failed to load quotes. Please try again.");
            }
        }
        finally {
            setLoading(false);
        }
    }, [user?.id]);
    useEffect(() => {
        fetchQuotes();
        fetchContactDetails();
    }, [fetchQuotes, fetchContactDetails]);
    // Scroll to section when hash is present (e.g. from Client Home click)
    useEffect(() => {
        const hash = location.hash?.slice(1);
        if (!hash)
            return;
        const validIds = ["requested", "for-approval", "accepted", "declined"];
        if (!validIds.includes(hash))
            return;
        const timer = setTimeout(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return () => clearTimeout(timer);
    }, [location.hash]);
    // Categorize quotes into sections based on status:
    // - Requested Quotes: status = "draft", "inprogress", or "cancelled" AND has quoteRequestId (client-initiated only)
    // - Quotes For Approval: status = "pending" (staff prepared, waiting for client approval)
    // - Accepted Quotes: status = "approved"
    // - Declined Quotes: status = "declined"
    const quotesBySection = useMemo(() => {
        const grouped = {
            requested: [],
            for_approval: [],
            accepted: [],
            declined: [],
        };
        quotes.forEach((quote) => {
            if ((matchesQuoteStatus(quote.status, "draft") ||
                matchesQuoteStatus(quote.status, "inprogress") ||
                matchesQuoteStatus(quote.status, "cancelled")) &&
                quote.quoteRequestId) {
                grouped.requested.push(quote);
            }
            else if (matchesQuoteStatus(quote.status, "pending")) {
                grouped.for_approval.push(quote);
            }
            else if (matchesQuoteStatus(quote.status, "approved")) {
                grouped.accepted.push(quote);
            }
            else if (matchesQuoteStatus(quote.status, "declined")) {
                grouped.declined.push(quote);
            }
        });
        return grouped;
    }, [quotes]);
    const handleViewQuote = (quote) => {
        if (isRequestedQuote(quote)) {
            navigate(`/client/quotes/${quote.id}/edit`);
        }
        else {
            navigate(`/client/quotes/${quote.id}/report`);
        }
    };
    const handleChat = (quoteId) => {
        navigate(`/client/notifications?quoteId=${quoteId}`);
    };
    const handleRequestQuote = () => {
        navigate("/client/quotes/new");
    };
    const handleApproveClick = (quote) => {
        setSelectedQuote(quote);
        setApprovalModalOpen(true);
    };
    const handleDeclineClick = (quote) => {
        setQuoteToDecline(quote);
    };
    const handleDeclineModalClose = useCallback(() => {
        setQuoteToDecline(null);
    }, []);
    const handleDeclineWithReason = useCallback(async (reason) => {
        if (!quoteToDecline)
            return;
        setActionLoading(quoteToDecline.id);
        try {
            await quotesService.updateQuoteStatus(quoteToDecline.id, "declined", {
                reasonForDecline: reason,
            });
            setQuoteToDecline(null);
            await fetchQuotes();
        }
        catch (err) {
            console.error("Error declining quote:", err);
            await alert("Failed to decline quote. Please try again.");
        }
        finally {
            setActionLoading(null);
        }
    }, [quoteToDecline, fetchQuotes, alert]);
    const handleApprovalSubmit = async (data) => {
        if (!selectedQuote || !user?.id)
            return;
        setActionLoading(selectedQuote.id);
        try {
            // Prepare update data with all acceptance fields
            // When approved, the quote will:
            // 1. Set status to "approved"
            // 2. Set approvedAt timestamp (auto-filled in View Quote date field)
            // 3. Store who accepted it (acceptedByContactId, acceptedByName, acceptedByPosition)
            // 4. Store the signature confirmation (acceptedBySignature)
            // 5. Store the purchase order number if provided
            const updateData = {
                status: "approved",
                acceptedByContactId: user.id,
                acceptedByName: data.billingContactName,
                acceptedByPosition: data.contactPosition,
                acceptedBySignature: data.signature,
                purchaseOrderNumber: data.purchaseOrder,
            };
            // Debug logging
            console.debug("Sending approval update:", {
                quoteId: selectedQuote.id,
                ...updateData,
            });
            // Update quote with status and acceptance data
            const response = await quotesService.updateQuote(selectedQuote.id, updateData);
            console.debug("Approval response:", response.data);
            // Auto-create Job Additional Work when client approves an Additional Works quote
            if (selectedQuote.quoteType === ADDITIONAL_WORKS_QUOTE_TYPE &&
                selectedQuote.mainJobId) {
                try {
                    let parentJobNumber = selectedQuote.jobNumber ?? null;
                    if (!parentJobNumber) {
                        const jobRes = await jobsService.getJob(selectedQuote.mainJobId);
                        parentJobNumber = jobRes.data?.jobNumber ?? null;
                    }
                    if (!parentJobNumber) {
                        await alert("Quote is misconfigured: could not resolve main job number.");
                    }
                    else {
                        const awRes = await additionalWorksService.getAdditionalWorksByJob(selectedQuote.mainJobId);
                        const raw = awRes.data;
                        const existingList = Array.isArray(raw)
                            ? raw
                            : (raw?.additionalWorks ??
                                raw?.AdditionalWorks ??
                                []);
                        const parseVSequence = (jobNum) => {
                            if (!jobNum || typeof jobNum !== "string")
                                return null;
                            const m = jobNum.match(/-V(\d+)$/i);
                            return m ? parseInt(m[1], 10) : null;
                        };
                        const existingVs = existingList
                            .map((w) => parseVSequence(w?.jobNumber))
                            .filter((n) => typeof n === "number");
                        const maxV = existingVs.length > 0 ? Math.max(...existingVs) : 0;
                        const sequence = maxV + 1;
                        const jobNumber = `${parentJobNumber}-V${sequence}`;
                        const duration = selectedQuote.projectDuration != null
                            ? Number(selectedQuote.projectDuration)
                            : undefined;
                        const commencementDateTime = toDateTimeLocal(selectedQuote.commencementDate, selectedQuote.commencementTime);
                        const completionDateTime = toDateTimeLocal(selectedQuote.completionDate, selectedQuote.completionTime);
                        await additionalWorksService.createAdditionalWork({
                            jobId: selectedQuote.mainJobId,
                            title: selectedQuote.title ?? "Additional Work",
                            description: selectedQuote.description ?? undefined,
                            estimatedCost: selectedQuote.totalAmount ?? undefined,
                            quoteId: selectedQuote.id,
                            jobNumber,
                            requestedById: response.data?.acceptedByContactId ?? user.id,
                            requestedByName: data.billingContactName,
                            commencementDate: commencementDateTime || undefined,
                            completionDate: completionDateTime || undefined,
                            duration: Number.isFinite(duration) ? duration : undefined,
                        });
                    }
                }
                catch (awErr) {
                    console.error("Error creating additional work:", awErr);
                    await alert("Quote was approved but creating the additional work record failed. Please contact support.");
                }
            }
            else if (selectedQuote.quoteType === ADDITIONAL_WORKS_QUOTE_TYPE &&
                !selectedQuote.mainJobId) {
                await alert("Quote is misconfigured: missing main job. Please contact support.");
            }
            await fetchQuotes();
            setApprovalModalOpen(false);
            setSelectedQuote(null);
        }
        catch (err) {
            console.error("Error approving quote:", err);
            await alert("Failed to approve quote. Please try again.");
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleDeleteQuote = (quote) => {
        // Find the index of the quote in requested quotes for proper image display
        const quoteIndex = quotesBySection.requested.findIndex((q) => q.id === quote.id);
        setQuoteToCancel(quote);
        setQuoteToCancelIndex(quoteIndex >= 0 ? quoteIndex : undefined);
        setCancelDialogOpen(true);
    };
    const handleCancelConfirm = async () => {
        if (!quoteToCancel)
            return;
        setActionLoading(quoteToCancel.id);
        try {
            await quotesService.updateQuoteStatus(quoteToCancel.id, "cancelled");
            await fetchQuotes();
            setCancelDialogOpen(false);
            setQuoteToCancel(null);
            setQuoteToCancelIndex(undefined);
        }
        catch (err) {
            console.error("Error cancelling quote:", err);
            await alert("Failed to cancel quote request. Please try again.");
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
        setQuoteToCancel(null);
        setQuoteToCancelIndex(undefined);
    };
    const requestedQuotes = quotesBySection.requested;
    const forApprovalQuotes = quotesBySection.for_approval;
    const acceptedQuotes = quotesBySection.accepted;
    const declinedQuotes = quotesBySection.declined;
    const isRequestedQuote = (q) => (matchesQuoteStatus(q.status, "draft") ||
        matchesQuoteStatus(q.status, "inprogress") ||
        matchesQuoteStatus(q.status, "cancelled")) &&
        !!q.quoteRequestId;
    const renderQuoteCard = (quote, index) => (_jsxs("div", { className: styles.quoteCard, children: [_jsxs("div", { className: styles.quoteTopSection, children: [_jsx("div", { className: styles.quoteImage, children: quote.imageUrl ? (_jsx("img", { src: quote.imageUrl, alt: `Quote ${quote.quoteNumber}`, onError: (e) => {
                                e.target.src =
                                    "/images/Defit Social Media 3.png";
                            } })) : index !== undefined ? (_jsx(LoopingImage, { imagePrefix: "/images/request-quote-default", count: index + 1, fallbackImage: "/images/empty-state-request.png", className: styles.quoteLoopingImage })) : (_jsx("div", { className: styles.quoteImagePlaceholder, children: _jsx(DocumentIcon, {}) })) }), _jsxs("div", { className: styles.quoteTopContent, children: [_jsx("div", { className: styles.quoteHeader, children: _jsx("div", { className: styles.quoteTitle, children: isRequestedQuote(quote)
                                        ? quote.quoteType === ADDITIONAL_WORKS_QUOTE_TYPE
                                            ? "Additional Works Quote Request"
                                            : "Quote Request"
                                        : quote.quoteType === ADDITIONAL_WORKS_QUOTE_TYPE
                                            ? `Additional Works Quote #${quote.quoteNumber}`
                                            : `Quote #${quote.quoteNumber}` }) }), quote.quoteType === ADDITIONAL_WORKS_QUOTE_TYPE &&
                                quote.jobNumber && (_jsx("div", { className: styles.quoteInfoItem, children: _jsxs("span", { children: ["Job ", quote.jobNumber] }) })), (formatQuoteAddress(quote) || quote.title) && (_jsx("div", { className: styles.quoteInfoItem, children: _jsx("span", { children: formatQuoteAddress(quote) || quote.title }) })), matchesQuoteStatus(quote.status, "draft") && (_jsx("div", { className: styles.quoteTopActions, children: _jsx("button", { className: styles.deleteBtn, "aria-label": "Delete quote", title: "Delete quote", onClick: () => handleDeleteQuote(quote), disabled: actionLoading === quote.id, children: _jsx(DeleteIcon, {}) }) }))] })] }), _jsx("div", { className: styles.quoteBottomSection, children: matchesQuoteStatus(quote.status, "cancelled") ? (_jsx("div", { className: styles.quoteBottomRow, children: _jsx("button", { className: styles.cancelledBtn, disabled: true, children: "CANCELLED" }) })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.quoteBottomRow, children: [_jsxs("button", { className: styles.chatBtn, onClick: () => handleChat(quote.id), disabled: actionLoading === quote.id, children: [_jsx(ChatIcon, {}), isRequestedQuote(quote)
                                            ? "EDIT QUOTE REQUEST"
                                            : "CHAT"] }), _jsx("button", { className: styles.viewQuoteBtn, onClick: () => handleViewQuote(quote), disabled: actionLoading === quote.id, children: isRequestedQuote(quote)
                                        ? "VIEW QUOTE REQUEST"
                                        : "VIEW QUOTE" })] }), matchesQuoteStatus(quote.status, "pending") && (_jsxs("div", { className: styles.quoteBottomRow, children: [_jsx("button", { className: styles.approveBtn, onClick: () => handleApproveClick(quote), disabled: actionLoading === quote.id, children: actionLoading === quote.id ? "..." : "APPROVE" }), _jsx("button", { className: styles.declineBtn, onClick: () => handleDeclineClick(quote), disabled: actionLoading === quote.id, children: actionLoading === quote.id ? "..." : "DECLINE" })] }))] })) })] }, quote.id));
    // Loading state
    if (loading) {
        return (_jsxs("div", { className: styles.page, children: [_jsx("div", { className: styles.contentHeader, children: _jsx("button", { className: styles.requestQuoteHeaderBtn, onClick: handleRequestQuote, children: "REQUEST QUOTE" }) }), _jsx("div", { className: styles.emptyState, children: _jsx("p", { children: "Loading quotes..." }) })] }));
    }
    // Error state
    if (error) {
        return (_jsxs("div", { className: styles.page, children: [_jsx("div", { className: styles.contentHeader, children: _jsx("button", { className: styles.requestQuoteHeaderBtn, onClick: handleRequestQuote, children: "REQUEST QUOTE" }) }), _jsxs("div", { className: styles.emptyState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchQuotes, style: {
                                marginTop: "var(--space-md)",
                                padding: "10px 20px",
                                border: "none",
                                borderRadius: "var(--radius-md)",
                                background: "var(--color-primary)",
                                color: "white",
                                cursor: "pointer",
                            }, children: "Try Again" })] })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.page, children: [_jsx("div", { className: styles.contentHeader, children: _jsx("button", { className: styles.requestQuoteHeaderBtn, onClick: handleRequestQuote, children: "REQUEST QUOTE" }) }), _jsxs("div", { className: styles.sectionsGrid, children: [_jsxs("section", { id: "requested", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "Requested Quotes" }) }), requestedQuotes.length === 0 ? (_jsx("div", { className: styles.quotesContainer, children: _jsxs("div", { className: styles.emptyState, children: [_jsx("img", { src: "/images/empty-state-request.png", alt: "No requested quotes", className: emptyStateStyles.image }), _jsx("h3", { className: styles.emptyStateHeading, children: "No requested quotes yet" }), _jsx("p", { className: styles.emptyStateText, children: "Request a quote to receive pricing and timelines for your project." }), _jsx("button", { className: styles.requestQuoteBtn, onClick: handleRequestQuote, children: "REQUEST QUOTE" })] }) })) : (_jsx("div", { className: styles.quotesContainer, children: _jsx("div", { className: styles.quotesGrid, children: requestedQuotes.map((quote, index) => renderQuoteCard(quote, index)) }) }))] }), _jsxs("section", { id: "for-approval", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "Quotes For Approval" }) }), forApprovalQuotes.length === 0 ? (_jsx("div", { className: styles.quotesContainer, children: _jsx("div", { className: styles.emptyState, children: _jsx(EmptyState, { image: "/images/empty-state-action.png", heading: "No quotes awaiting approval", text: "Quotes will appear here once they are prepared and ready for your review." }) }) })) : (_jsx("div", { className: styles.quotesContainer, children: _jsx("div", { className: styles.quotesGrid, children: forApprovalQuotes.map((quote, index) => renderQuoteCard(quote, index)) }) }))] }), _jsxs("section", { id: "accepted", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "Accepted Quotes" }) }), acceptedQuotes.length === 0 ? (_jsx("div", { className: styles.quotesContainer, children: _jsx("div", { className: styles.emptyState, children: _jsx(EmptyState, { image: "/images/empty-state-invoice.png", heading: "No accepted quotes yet", text: "Approved quotes will be shown here once you accept them." }) }) })) : (_jsx("div", { className: styles.quotesContainer, children: _jsx("div", { className: styles.quotesGrid, children: acceptedQuotes.map((quote, index) => renderQuoteCard(quote, index)) }) }))] }), _jsxs("section", { id: "declined", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "Declined Quotes" }) }), declinedQuotes.length === 0 ? (_jsx("div", { className: styles.quotesContainer, children: _jsx("div", { className: styles.emptyState, children: _jsx(EmptyState, { image: "/images/empty-state-decline.png", heading: "No declined quotes", text: "Quotes you choose not to approve will appear here." }) }) })) : (_jsx("div", { className: styles.quotesContainer, children: _jsx("div", { className: styles.quotesGrid, children: declinedQuotes.map((quote, index) => renderQuoteCard(quote, index)) }) }))] })] })] }), selectedQuote && (_jsx(QuoteApprovalModal, { isOpen: approvalModalOpen, onClose: () => {
                    setApprovalModalOpen(false);
                    setSelectedQuote(null);
                }, quoteNumber: selectedQuote.quoteNumber, onApprove: handleApprovalSubmit, contactName: contactDetails?.name, contactEmail: contactDetails?.email, contactPosition: contactDetails?.position, companyName: contactDetails?.company })), quoteToCancel && (_jsx(CancelQuoteDialog, { isOpen: cancelDialogOpen, quote: quoteToCancel, quoteIndex: quoteToCancelIndex, onConfirm: handleCancelConfirm, onCancel: handleCancelDialogClose })), quoteToDecline && (_jsx(QuoteDeclineReasonModal, { isOpen: !!quoteToDecline, onClose: handleDeclineModalClose, onSubmit: handleDeclineWithReason, isSubmitting: actionLoading === quoteToDecline.id, quoteNumber: quoteToDecline.quoteNumber }))] }));
};
